'use client'

import { api } from '@/api/v2/api'
import useAuth from '@/hooks/use-auth'
import type { Notification, NotificationType } from '@/types/notification'
import { useInfiniteQuery } from '@tanstack/react-query'

export default function useNotifications(type: NotificationType) {
  const { isAuthenticated } = useAuth()

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ['notifications', type],
      queryFn: async ({
        pageParam = 0,
      }): Promise<{
        items: Notification[]
        totalItems: number
        nextCursor: number | null
      }> => {
        const { data } = await api.get(
          `/user/notifications/${type}?cursor=${pageParam}&limit=10&sortBy=date`
        )

        let items: Notification[]
        let totalItems: number

        if (type === 'activity') {
          items = data.activityItems ?? []
          totalItems = data.activityCount ?? 0
        } else {
          items = data.alertItems ?? []
          totalItems = data.alertCount ?? 0
        }

        const nextCursor =
          pageParam + items.length < totalItems
            ? pageParam + items.length
            : null

        return { items, totalItems, nextCursor }
      },
      getNextPageParam: (lastPage) => {
        return lastPage.nextCursor
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isAuthenticated,
    })

  const notifications = data
    ? data.pages.flatMap(({ items }) => items)
    : undefined

  return {
    notifications,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
  }
}
